import {
  type ListSuggestionsProvider,
  listSuggestions,
  withLeading,
} from "@/shared/components/filters/suggestions";
import type { FilterDefinition } from "@/shared/components/ResourceView";

import type { BucketWindow } from "./bucketWindow";

/**
 * The distinct series a grouped response holds, in first-seen order, `null`
 * standing for the "Not set" series. `undefined` for an ungrouped response,
 * whose rows carry no group key.
 */
export function seriesKeysOf(
  rows: readonly { group_key?: string | null }[],
): (string | null)[] | undefined {
  const keys = new Set<string | null>();
  for (const row of rows) {
    if (row.group_key !== undefined) {
      keys.add(row.group_key);
    }
  }
  return keys.size > 0 ? [...keys] : undefined;
}

/**
 * How many sessions each series holds in what a drill-down was opened on,
 * `null` keying the "Not set" series: the same rows its table lists.
 */
export type SeriesCounts = ReadonlyMap<string | null, number>;

/**
 * Each value's sessions in an aggregate response's `bucket`: the `total` of
 * every record starting inside it. `undefined` for an ungrouped response. A
 * drill-down counts its rows' values from it (see `bucketValues`).
 */
export function bucketSeriesCounts(
  records: readonly {
    start_time: bigint;
    total: number;
    group_key?: string | null;
  }[],
  bucket: BucketWindow,
): SeriesCounts | undefined {
  const counts = new Map<string | null, number>();
  for (const record of records) {
    if (
      record.group_key !== undefined &&
      record.start_time >= bucket.start_time &&
      record.start_time < bucket.end_time
    ) {
      counts.set(
        record.group_key,
        (counts.get(record.group_key) ?? 0) + record.total,
      );
    }
  }
  return records.some((record) => record.group_key !== undefined)
    ? counts
    : undefined;
}

/**
 * Each series' sessions among a raw card's clicked points, each session
 * counted once and a point without one counted as its own. `undefined` for an
 * ungrouped chart.
 */
export function selectionSeriesCounts(
  selection: readonly { session_id?: string; group_key?: string | null }[],
): SeriesCounts | undefined {
  const sessions = new Map<string | null, Set<unknown>>();
  for (const point of selection) {
    if (point.group_key === undefined) {
      continue;
    }
    const seen = sessions.get(point.group_key) ?? new Set();
    seen.add(point.session_id ?? point);
    sessions.set(point.group_key, seen);
  }
  if (sessions.size === 0) {
    return undefined;
  }
  return new Map([...sessions].map(([key, seen]) => [key, seen.size]));
}

/**
 * The dashboard's filter rows, with the group-by row suggesting the chart's
 * own series first, ahead of whatever that row already suggests (an org-wide
 * device or custom-field search). The unset series becomes `hasUnset`, so
 * "Not set" is offered, never a value named after it.
 *
 * The series come from the chart, not from the drill-down's chips, so a
 * series the reader removed from the chip is offered again.
 *
 * With `seriesCounts`, each series carries its count (0 for one the chart
 * draws but the selection lacks) and they are listed most popular first. The
 * counts are the selection's, so they replace the row's own (org-wide) ones
 * rather than mixing with them (see `withLeading`).
 */
export function withSeriesSuggestions(
  definitions: FilterDefinition[] | undefined,
  groupBy: string | undefined,
  seriesKeys: readonly (string | null)[] | undefined,
  seriesCounts?: SeriesCounts,
): FilterDefinition[] | undefined {
  if (
    definitions === undefined ||
    groupBy === undefined ||
    seriesKeys === undefined
  ) {
    return definitions;
  }
  const hasUnset = seriesKeys.includes(null);
  const values = seriesKeys.filter((key): key is string => key !== null);
  const series =
    seriesCounts === undefined
      ? listSuggestions(values, { hasUnset })
      : countedSeries(values, hasUnset, seriesCounts);
  return definitions.map((definition) =>
    definition.field === groupBy && definition.type === "string"
      ? {
          ...definition,
          suggestions: withLeading(series, definition.suggestions),
        }
      : definition,
  );
}

/** The series as suggestions counted by `seriesCounts`, most popular first. */
function countedSeries(
  values: readonly string[],
  hasUnset: boolean,
  seriesCounts: SeriesCounts,
): ListSuggestionsProvider {
  const countOf = (key: string | null) => seriesCounts.get(key) ?? 0;
  // A stable sort, so tied series keep the chart's order.
  const ranked = [...values].sort((a, b) => countOf(b) - countOf(a));
  return listSuggestions(ranked, {
    hasUnset,
    counts: Object.fromEntries(ranked.map((key) => [key, countOf(key)])),
    unsetCount: hasUnset ? countOf(null) : undefined,
  });
}
